import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { importFromYaml } from "../../../abstract/controller/modelYamlImport";
import * as configUtils from "../../../abstract/controller/utils/configUtils";
import * as systemUtils from "../../../abstract/controller/utils/systemUtils";
import { getEndpointAI, getEndpointTomato } from "../../../abstract/redux/slice/endpointSlice";
import { setSpinner, setSpinnerMessage } from "../../../abstract/redux/slice/overlaySlice";
import { setRoot } from "../../../abstract/redux/slice/structureSlice";
import { ButtonDefault } from "../../ui/buttons/ButtonDefault";
import styles from "./AsideElementOptionsDev.module.scss";

const modelYamlInitial = "./example/initial.yaml";
const modelYamlDefault = "./example/default.yaml";
const modelYamlWorkshop = "./example/workshop.yaml";
const modelYamlEpicBattle = "./example/epicbattle.yaml";

function AsideElementOptionsDev() {
  const _dispatch = useDispatch();

  const _fileRef = useRef(null);

  const handleFileChange = async (event) => {
    const _file = event.target.files && event.target.files[0];

    if (!_file) {
      return;
    }

    _dispatch(setSpinner(true));
    _dispatch(setSpinnerMessage("loading"));

    const _content = await systemUtils.readTextFromFile(_file);
    const { model: _results, error: _error } = importFromYaml(_content);

    if (!_results) {
      console.log(`The file could not be imported! ${_error}`);
    } else {
      _dispatch(setRoot(_results, 0));
      _dispatch(setSpinner(false));
    }

    event.target.value = null;
  };

  const handleReset = () => {
    handleResetInitial();

    systemUtils.clearSessionStorage();
    systemUtils.dispatchEvent("clearStorage");
  };

  const handleResetInitial = () => {
    _dispatch(setSpinner(true));
    _dispatch(setSpinnerMessage("parsing"));

    fetch(modelYamlInitial).then(async (e) => {
      const _content = await e.text();
      const { model: _results, error: _error } = importFromYaml(_content);

      if (!_results) {
        alert(`The file could not be imported! ${_error}`);
        console.log(`The file could not be imported! ${_error}`);

        _dispatch(setRoot({}, 0));
        _dispatch(setSpinner(false));
      } else {
        _dispatch(setRoot(_results, 0));
        _dispatch(setSpinner(false));
      }
    });
  };

  const handleResetDefault = () => {
    _dispatch(setSpinner(true));
    _dispatch(setSpinnerMessage("parsing"));

    fetch(modelYamlDefault).then(async (e) => {
      const _content = await e.text();
      const { model: _results, error: _error } = importFromYaml(_content);

      if (!_results) {
        alert(`The file could not be imported! ${_error}`);
        console.log(`The file could not be imported! ${_error}`);

        _dispatch(setRoot({}, 0));
        _dispatch(setSpinner(false));
      } else {
        _dispatch(setRoot(_results, 0));
        _dispatch(setSpinner(false));
      }
    });
  };

  const handleResetWorkshop = () => {
    _dispatch(setSpinner(true));
    _dispatch(setSpinnerMessage("parsing"));

    fetch(modelYamlWorkshop).then(async (e) => {
      const _content = await e.text();
      const { model: _results, error: _error } = importFromYaml(_content);

      if (!_results) {
        alert(`The file could not be imported! ${_error}`);
        console.log(`The file could not be imported! ${_error}`);

        _dispatch(setRoot({}, 0));
        _dispatch(setSpinner(false));
      } else {
        _dispatch(setRoot(_results, 0));
        _dispatch(setSpinner(false));
      }
    });
  };

  const handleResetEpicBattle = () => {
    _dispatch(setSpinner(true));
    _dispatch(setSpinnerMessage("parsing"));

    fetch(modelYamlEpicBattle).then(async (e) => {
      const _content = await e.text();
      const { model: _results, error: _error } = importFromYaml(_content);

      if (!_results) {
        alert(`The file could not be imported! ${_error}`);
        console.log(`The file could not be imported! ${_error}`);

        _dispatch(setRoot({}, 0));
        _dispatch(setSpinner(false));
      } else {
        _dispatch(setRoot(_results, 0));
        _dispatch(setSpinner(false));
      }
    });
  };

  return (
    <div className={styles["options"]}>
      <input
        style={{ display: "none" }}
        ref={_fileRef}
        type="file"
        accept=".yaml"
        onChange={handleFileChange}
      />
      <ButtonDefault text={"global reset"} handler={handleReset} shadow={false} wide />
      <ButtonDefault text={"model - initial"} handler={handleResetInitial} shadow={false} wide />
      <ButtonDefault text={"model - default"} handler={handleResetDefault} shadow={false} wide />
      <ButtonDefault text={"model - workshop"} handler={handleResetWorkshop} shadow={false} wide />
      <ButtonDefault
        text={"model - epic battle"}
        handler={handleResetEpicBattle}
        shadow={false}
        wide
      />
      <TextEndpointAI />
      <TextEndpointTomato />
      <EndpointAICheckbox name={"Force local endpoint (8080) - AI"} />
      <EndpointTomatoCheckbox name={"Force local endpoint (9000) - Tomato"} />
      <ConfigCheckbox name={"Send mock suggestions (uuid, offline)"} />
    </div>
  );
}

function TextEndpointAI() {
  const endpoint = useSelector(getEndpointAI);

  return (
    <>
      <div className={styles["text"]}>
        Endpoint - AI
        <input type="text" value={endpoint} disabled />
      </div>
    </>
  );
}

function TextEndpointTomato() {
  const endpoint = useSelector(getEndpointTomato);

  return (
    <>
      <div className={styles["text"]}>
        Endpoint - Tomato
        <input type="text" value={endpoint} disabled />
      </div>
    </>
  );
}

function ConfigCheckbox({ name }) {
  const [_isEnabled, _setEnabled] = useState(() => configUtils.getMockSuggestions());

  const handleOnChange = (e) => {
    const value = !_isEnabled;

    _setEnabled(value);
    configUtils.setMockSuggestions(value);
  };

  return (
    <label className={styles["checkbox"]}>
      <input type="checkbox" checked={_isEnabled} onChange={handleOnChange} />
      &nbsp;{name}
    </label>
  );
}

function EndpointAICheckbox({ name }) {
  const [_isEnabled, _setEnabled] = useState(() => configUtils.getEndpointLocalAI());

  const handleOnChange = (e) => {
    const value = !_isEnabled;

    _setEnabled(value);
    configUtils.setEndpointLocalAI(value);
  };

  return (
    <label className={styles["checkbox"]}>
      <input type="checkbox" checked={_isEnabled} onChange={handleOnChange} />
      &nbsp;{name}
    </label>
  );
}

function EndpointTomatoCheckbox({ name }) {
  const [_isEnabled, _setEnabled] = useState(() => configUtils.getEndpointLocalTomato());

  const handleOnChange = (e) => {
    const value = !_isEnabled;

    _setEnabled(value);
    configUtils.setEndpointLocalTomato(value);
  };

  return (
    <label className={styles["checkbox"]}>
      <input type="checkbox" checked={_isEnabled} onChange={handleOnChange} />
      &nbsp;{name}
    </label>
  );
}

export default AsideElementOptionsDev;
